import React from "react";

// Token types become the CSS classes tok-<type>, colored by DlvYamlEditor.
// The tokens of a text always join back to the text itself: the editor overlays them on the textarea.

const BLOCK_SCALAR = /^([|>][+-]?\d*)(\s+#.*)?$/;
const KEY = /^("[^"]*"|'[^']*'|[^\s#:"'][^#:]*?)(\s*:)(?=\s|$)/;
const NUMBER = /^-?\d+(\.\d+)?$/;
const KEYWORD = /^(true|false|null|~)$/;

// Facts and expected atoms are ASP: comments, strings, "not", predicates, variables, numbers
export function aspTokens(text) {
    const pattern = /(%.*$)|("(?:[^"\\]|\\.)*")|(\bnot\b)|([a-z][A-Za-z0-9_]*)(?=\()|(\b[A-Z][A-Za-z0-9_]*\b|\b_\b)|(\b\d+\b)|(:-|[().,])|(#[a-z]+)/g;
    const tokens = [];
    let last = 0;
    let match;
    while ((match = pattern.exec(text)) !== null) {
        if (match.index > last) {
            tokens.push({text: text.slice(last, match.index)});
        }
        let type = 'punct';
        if (match[1]) type = 'comment';
        else if (match[2]) type = 'string';
        else if (match[3]) type = 'keyword';
        else if (match[4]) type = 'predicate';
        else if (match[5]) type = 'variable';
        else if (match[6]) type = 'number';
        else if (match[8]) type = 'keyword';
        tokens.push({text: match[0], type});
        last = match.index + match[0].length;
    }
    if (last < text.length) {
        tokens.push({text: text.slice(last)});
    }
    return tokens;
}

// Index of a trailing "# comment" (a # preceded by a space, outside quotes), or -1
function commentStart(text) {
    let quote = null;
    for (let i = 0; i < text.length; i++) {
        const c = text[i];
        if (quote) {
            if (c === quote) quote = null;
        } else if (c === '"' || c === "'") {
            quote = c;
        } else if (c === '#' && (i === 0 || /\s/.test(text[i - 1]))) {
            return i;
        }
    }
    return -1;
}

function valueTokens(text, isAtom) {
    const tokens = [];
    const start = commentStart(text);
    const body = start === -1 ? text : text.slice(0, start);
    const comment = start === -1 ? '' : text.slice(start);

    const leading = body.length - body.trimStart().length;
    const trailing = body.length - body.trimEnd().length;
    const value = body.trim();
    if (leading > 0) tokens.push({text: body.slice(0, leading)});

    if (value !== '') {
        if (value[0] === '"' || value[0] === "'") {
            tokens.push({text: value, type: 'string'});
        } else if (KEYWORD.test(value)) {
            tokens.push({text: value, type: 'keyword'});
        } else if (NUMBER.test(value)) {
            tokens.push({text: value, type: 'number'});
        } else if (isAtom) {
            tokens.push(...aspTokens(value));
        } else {
            tokens.push({text: value});
        }
    }
    if (trailing > 0 && value !== '') tokens.push({text: body.slice(body.length - trailing)});
    if (comment) tokens.push({text: comment, type: 'comment'});
    return tokens;
}

function yamlLineTokens(line, state) {
    if (state.blockIndent !== null) {
        if (line.trim() === '') {
            return [{text: line}];
        }
        if (line.length - line.trimStart().length > state.blockIndent) {
            return aspTokens(line);
        }
        state.blockIndent = null;
    }

    const trimmed = line.trimStart();
    if (trimmed === '') {
        return [{text: line}];
    }
    if (trimmed.startsWith('#')) {
        return [{text: line, type: 'comment'}];
    }

    const tokens = [];
    const lead = /^(\s*)((?:-\s+)*)/.exec(line);
    if (lead[1]) tokens.push({text: lead[1]});
    if (lead[2]) {
        const dashes = lead[2].match(/-\s+/g);
        dashes.forEach(dash => {
            tokens.push({text: '-', type: 'punct'});
            tokens.push({text: dash.slice(1)});
        });
    }

    const rest = line.slice(lead[0].length);
    const key = KEY.exec(rest);
    if (!key) {
        tokens.push(...valueTokens(rest, true));
        return tokens;
    }

    tokens.push({text: key[1], type: 'key'});
    tokens.push({text: key[2], type: 'punct'});

    const value = rest.slice(key[0].length);
    const block = BLOCK_SCALAR.exec(value.trim());
    if (block) {
        const leadingSpaces = value.length - value.trimStart().length;
        tokens.push({text: value.slice(0, leadingSpaces)});
        tokens.push({text: block[1], type: 'indicator'});
        const afterIndicator = value.trimStart().slice(block[1].length);
        if (afterIndicator) tokens.push({text: afterIndicator, type: 'comment'});
        state.blockIndent = lead[1].length + (lead[2] ? lead[2].length : 0);
    } else {
        tokens.push(...valueTokens(value, false));
    }
    return tokens;
}

export function tokenizeYaml(text) {
    const state = {blockIndent: null};
    return text.split('\n').map(line => yamlLineTokens(line, state));
}

export function highlightYaml(text) {
    const lines = tokenizeYaml(text);
    const nodes = [];
    lines.forEach((tokens, lineIndex) => {
        tokens.forEach((token, tokenIndex) => {
            nodes.push(token.type
                ? <span key={`${lineIndex}-${tokenIndex}`} className={`tok-${token.type}`}>{token.text}</span>
                : token.text);
        });
        if (lineIndex < lines.length - 1) {
            nodes.push('\n');
        }
    });
    return nodes;
}

export function highlightAsp(text) {
    return text.split('\n').flatMap((line, lineIndex, lines) => {
        const nodes = aspTokens(line).map((token, tokenIndex) => token.type
            ? <span key={`${lineIndex}-${tokenIndex}`} className={`tok-${token.type}`}>{token.text}</span>
            : token.text);
        return lineIndex < lines.length - 1 ? [...nodes, '\n'] : nodes;
    });
}
